import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Col, Container, FloatingLabel, Form, Modal, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import Notification from '../components/Notification'
import StorageLabelsPrintTrigger from '../components/StorageLabelsPrintTrigger'
import StorageProtocolPrintTrigger from '../components/StorageProtocolPrintTrigger'
import pneuLetoIcon from '../assets/images/icons/pneu-leto.png'
import pneuZimaIcon from '../assets/images/icons/pneu-zima.png'
import pneuCelorokIcon from '../assets/images/icons/pneu-celorok.png'
import diskBezIcon from '../assets/images/icons/disk-bez.png'
import diskOcelIcon from '../assets/images/icons/disk-ocel.png'
import diskHlinikIcon from '../assets/images/icons/disk-hlinik.png'
import {
  createUschovaPrijem,
  fetchUschovaDalsiCislo,
  fetchUschovaDetail,
  fetchUschovaNapoveda,
  issueUschovaZakazka,
  updateUschovaVozidlo,
  updateUschovaZakazka,
  updateUschovaZakaznik,
} from '../services/api/uschovy'

const toTrim = (v) => String(v ?? '').trim()
const toUpper = (v) => String(v ?? '').toLocaleUpperCase('cs-CZ')
const formatDepth = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'))
  return Number.isFinite(n) ? String(n).replace('.', ',') : ''
}
const normalizeDepthInput = (v) => {
  const cleaned = String(v ?? '').replace('.', ',').replace(/[^0-9,]/g, '')
  const commaIndex = cleaned.indexOf(',')
  if (commaIndex < 0) return cleaned
  return `${cleaned.slice(0, commaIndex).replace(/,/g, '')},${cleaned.slice(commaIndex + 1).replace(/,/g, '').slice(0, 1)}`
}
const toNum = (v, fallback = 0) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}
const toBool = (v) => Number(v) === 1

const formatDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = String(d.getFullYear())
  return `${dd}.${mm}.${yyyy}`
}
const dateDaySortValue = (value) => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return 0
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate()
}
const formatStorageDays = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return '-'
  if (n === 1) return '1 den'
  if (n >= 2 && n <= 4) return `${n} dny`
  return `${n} dnů`
}

const vehicleTypeLabel = (v) => {
  const n = Number(v)
  if (n === 1) return 'osobní'
  if (n === 2) return 'suv/4x4'
  if (n === 3) return 'dodávka'
  if (n === 4) return 'truck'
  if (n === 5) return 'moto'
  return ''
}

const seasonLabel = (v) => {
  const n = Number(v)
  if (n === 1) return 'letní'
  if (n === 2) return 'zimní'
  if (n === 3) return 'celoroční'
  return ''
}

const diskLabel = (v) => {
  const n = Number(v)
  if (n === 1) return 'ocel'
  if (n === 2) return 'hliník'
  return 'bez disku'
}

const smerLabel = (v) => {
  const s = String(v ?? '').trim().toLowerCase()
  if (s === '1' || s === 'prijem' || s === 'příjem') return 'přijato do úschovy'
  if (s === '2' || s === 'vydej' || s === 'výdej') return 'vydáno z úschovy'
  return 'změna v úschově'
}

const typVydejeLabel = (v) => {
  const s = String(v ?? '').trim().toLowerCase()
  if (s === '1' || s === 'montaz' || s === 'montáž') return 'montáž'
  if (s === '2' || s === 'kufr') return 'do kufru'
  if (s === '3' || s === 'likvidace') return 'likvidace'
  return '-'
}

const toDateInputValue = (value) => {
  if (!value) return ''
  const iso = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  if (/^\d{4}-\d{2}-\d{2}\s/.test(iso)) return iso.slice(0, 10)
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const DEFAULT_TIRE_POSITIONS = ['LP', 'PP', 'LZ', 'PZ']
const tirePositionForIndex = (index) => DEFAULT_TIRE_POSITIONS[index] || ''
const emptyPneuRow = (pozice = '', canRemove = false) => ({
  _canRemove: canRemove,
  pozice,
  znacka: '',
  rozmer: '',
  model: '',
  hloubka: '',
  sezona: '',
  disk: '',
})
const emptyPneuTemplate = () => ({ znacka: '', rozmer: '', model: '', hloubka: '', sezona: '', disk: '' })

const seasonIcons = { 1: pneuLetoIcon, 2: pneuZimaIcon, 3: pneuCelorokIcon }
const diskIcons = { 0: diskBezIcon, 1: diskOcelIcon, 2: diskHlinikIcon }
const iconSelectStyle = (image) => ({
  backgroundImage: `url(${image})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: '26px 26px',
  height: '54.5px',
})
const tireControlStyle = { height: '54.5px' }

const StorageZakazkaBlock = ({ zak, polozky, history = false, onEdit, onIssue }) => {
  const datumPrijmu = formatDate(zak?.datum_prijmu || zak?.datum)
  const datumVydeje = formatDate(zak?.datum_vydeje)
  const historyHeaderCols = [
    { value: toTrim(zak.cislo) || '-', label: 'zakázka' },
    { value: toTrim(zak.pozice) || '-', label: 'pozice' },
    { value: datumPrijmu || '-', label: 'datum příjmu' },
    { value: datumVydeje || '-', label: 'datum výdeje' },
    { value: formatStorageDays(zak.delka_uschovy_dny), label: 'délka úschovy' },
    { value: typVydejeLabel(zak.typ_vydeje), label: 'způsob výdeje' },
  ]
  return (
    <div className="storage-card-surface rounded-3 p-4">
      <Row className="g-1 mb-2 align-items-center">
        <Col md={history ? 10 : 4}>
          {!history ? (
            <Row className="g-0 align-items-center">
              <Col xs={4} className="pe-3 border-end border-secondary-subtle">
                <div className="fw-semibold lh-1 ps-1">{toTrim(zak.cislo) || '-'}</div>
                <div className="small text-muted lh-1.2 ps-1">zakázka</div>
              </Col>
              <Col xs={4} className="px-3 border-end border-secondary-subtle">
                <div className="fw-semibold lh-1">{toTrim(zak.pozice) || '-'}</div>
                <div className="small text-muted lh-1.2">pozice</div>
              </Col>
              <Col xs={4} className="px-3">
                <div className="fw-semibold lh-1">{datumPrijmu || '-'}</div>
                <div className="small text-muted lh-1.2">přijato</div>
              </Col>
            </Row>
          ) : (
            <Row className="g-0 align-items-center">
              {historyHeaderCols.map((col, idx) => (
                <Col
                  key={`${zak.id_zakazky}-history-col-${idx}`}
                  xs={12}
                  md={2}
                  className={`${idx > 0 ? 'ps-3 border-start border-secondary-subtle' : 'pe-3'}`}
                >
                  <div className="fw-semibold lh-1">{col.value}</div>
                  <div className="small text-muted lh-1.2">{col.label}</div>
                </Col>
              ))}
            </Row>
          )}
        </Col>
        <Col md={history ? 2 : 8} className="pe-0">
          {history ? (
            <div className="d-flex justify-content-md-end">
              <Button className="p-2 opacity-75" size="sm" type="button" title="Úprava záznamu" onClick={() => onEdit?.(zak)} variant="primary">
                <i className="bi bi-pencil-square me-2"></i>Úprava
              </Button>
            </div>
          ) : (
            <div className="d-flex flex-wrap justify-content-md-end align-items-center gap-1">
              <span className="small text-muted me-1">výdej</span>
              <Button className="p-2 opacity-75" size="sm" type="button" title="Výdej pneu na vozidlo" onClick={() => onIssue?.(zak, 'montaz')} variant="success">
                <i className="bi bi-wrench me-2"></i>Montáž
              </Button>
              <Button className="p-2 opacity-75" size="sm" type="button" title="Vrácení pneu zákazníkovi bez montáže" onClick={() => onIssue?.(zak, 'kufr')} variant="warning">
                <i className="bi bi-bag me-2"></i>Kufr
              </Button>
              <Button className="p-2 opacity-75" size="sm" type="button" title="Likvidace pneu" onClick={() => onIssue?.(zak, 'likvidace')} variant="danger">
                <i className="bi bi-trash3 me-2"></i>Likvidace
              </Button>
              <span className="border-start border-secondary-subtle align-self-stretch mx-2"></span>
              <Button className="p-2 opacity-75" size="sm" type="button" title="Úprava záznamu" onClick={() => onEdit?.(zak)} variant="primary">
                <i className="bi bi-pencil-square me-2"></i>Úprava
              </Button>
              <StorageLabelsPrintTrigger
                zakazkaId={toNum(zak?.id_zakazky)}
                renderButton={({ onClick, disabled }) => (
                  <Button
                    className="p-2 opacity-75"
                    size="sm"
                    type="button"
                    variant="primary"
                    title="Tisk štítků pneumatik"
                    onClick={onClick}
                    disabled={disabled}
                  >
                    <i className="bi bi-printer me-2"></i>Štítky
                  </Button>
                )}
              />
              <StorageProtocolPrintTrigger
                zakazkaId={toNum(zak?.id_zakazky)}
                renderButton={({ onClick, disabled }) => (
                  <Button
                    className="p-2 opacity-75"
                    size="sm"
                    type="button"
                    variant="primary"
                    title="Tisk protokolu o úschově"
                    onClick={onClick}
                    disabled={disabled}
                  >
                    <i className="bi bi-printer me-2"></i>Protokol
                  </Button>
                )}
              />
            </div>
          )}
        </Col>
      </Row>

      <div className="d-grid gap-1">
        <Row className="g-1 px-1 text-muted small d-none d-md-flex">
          <Col md={1}>pozice</Col>
          <Col md={3}>značka</Col>
          <Col md={2}>rozměr</Col>
          <Col md={3}>model</Col>
          <Col md={1}>hloubka</Col>
          <Col md={1} className="text-center">sezóna</Col>
          <Col md={1} className="text-center">disk</Col>
        </Row>
        {polozky.map((p) => (
          <Row key={`${zak.id_zakazky}-${p.id_pneumatiky}`} className="g-1">
            <Col md={1}>
              <Form.Control readOnly style={tireControlStyle} value={toTrim(p.pozice) || '-'} aria-label="Pozice" />
            </Col>
            <Col md={3}>
              <Form.Control readOnly style={tireControlStyle} value={toTrim(p.znacka)} aria-label="Značka" />
            </Col>
            <Col md={2}>
              <Form.Control readOnly style={tireControlStyle} value={toTrim(p.rozmer)} aria-label="Rozměr" />
            </Col>
            <Col md={3}>
              <Form.Control readOnly style={tireControlStyle} value={toTrim(p.model)} aria-label="Model" />
            </Col>
            <Col md={1}>
              <Form.Control readOnly style={tireControlStyle} value={formatDepth(p.hloubka)} aria-label="Hloubka" />
            </Col>
            <Col md={1}>
              <div className="storage-card-icon-cell" title={`Sezóna: ${seasonLabel(p.sezona)}`}>
                <img src={seasonIcons[p.sezona]} alt={seasonLabel(p.sezona)} />
              </div>
            </Col>
            <Col md={1}>
              <div className="storage-card-icon-cell" title={`Disk: ${diskLabel(p.disk)}`}>
                <img src={diskIcons[p.disk]} alt={diskLabel(p.disk)} />
              </div>
            </Col>
          </Row>
        ))}
        <Row className="g-1">
          <Col md={5}>
            <FloatingLabel label="poznámka">
              <Form.Control readOnly value={toTrim(zak.poznamka)} />
            </FloatingLabel>
          </Col>
          <Col md={4}>
            <div className="h-100 d-flex align-items-center gap-3 px-2">
              <div className="small" title="TMPS">
                <i className={`bi ${toBool(zak.tmps) ? 'bi-check-square-fill text-primary' : 'bi-square text-muted'}`}></i>
                <span className="ms-1 text-muted">tmps</span>
              </div>
              <div className="small" title="Poklice">
                <i className={`bi ${toBool(zak.poklice) ? 'bi-check-square-fill text-primary' : 'bi-square text-muted'}`}></i>
                <span className="ms-1 text-muted">poklice</span>
              </div>
              <div className="small" title="Šrouby">
                <i className={`bi ${toBool(zak.srouby) ? 'bi-check-square-fill text-primary' : 'bi-square text-muted'}`}></i>
                <span className="ms-1 text-muted">šrouby</span>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}

const StorageDetail = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const id = Number(location.state?.id) || 0

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState({
    vehicle: null,
    customer: null,
    zakazky: [],
    pneumatikyByZakazka: [],
  })

  const [notifications, setNotifications] = useState([])

  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [validatedVehicle, setValidatedVehicle] = useState(false)
  const [isSavingVehicle, setIsSavingVehicle] = useState(false)
  const [vehicleSuggestions, setVehicleSuggestions] = useState({ znacka: [] })
  const vehicleSuggestTimersRef = useRef({})
  const [vehicleEdit, setVehicleEdit] = useState({
    spz: '',
    znacka: '',
    rok_vyroby: '',
    typ: '1',
    pocet_pneu: '',
    poznamka: '',
  })

  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [validatedCustomer, setValidatedCustomer] = useState(false)
  const [isSavingCustomer, setIsSavingCustomer] = useState(false)
  const [customerEdit, setCustomerEdit] = useState({
    firma: '',
    jmeno: '',
    ulice: '',
    psc: '',
    mesto: '',
    ico: '',
    email: '',
    telefon: '',
    poznamka: '',
  })

  const [showZakazkaModal, setShowZakazkaModal] = useState(false)
  const [zakazkaMode, setZakazkaMode] = useState('edit')
  const [validatedZakazka, setValidatedZakazka] = useState(false)
  const [isSavingZakazka, setIsSavingZakazka] = useState(false)
  const [pneuSuggestions, setPneuSuggestions] = useState({ znacka: [], rozmer: [], model: [] })
  const [pneuTemplate, setPneuTemplate] = useState(emptyPneuTemplate())
  const pneuSuggestTimersRef = useRef({})
  const [zakazkaEdit, setZakazkaEdit] = useState({
    id_zakazky: 0,
    cislo: '',
    pozice: '',
    datum: '',
    poznamka: '',
    tmps: false,
    poklice: false,
    srouby: false,
    polozky: [],
  })
  const [showIssueModal, setShowIssueModal] = useState(false)
  const [isIssuing, setIsIssuing] = useState(false)
  const [issueCtx, setIssueCtx] = useState({ id_zakazky: 0, cislo: '', typ: '' })

  useEffect(() => {
    return () => {
      Object.values(pneuSuggestTimersRef.current || {}).forEach((timerId) => {
        if (timerId) clearTimeout(timerId)
      })
      Object.values(vehicleSuggestTimersRef.current || {}).forEach((timerId) => {
        if (timerId) clearTimeout(timerId)
      })
    }
  }, [])

  const loadVehicleSuggestions = useCallback((field, value) => {
    const fieldMap = {
      znacka: 'vehicle_znacka',
    }
    const apiField = fieldMap[field]
    if (!apiField) return

    const query = toTrim(value)
    if (query.length < 3) {
      setVehicleSuggestions((prev) => ({ ...prev, [field]: [] }))
      if (vehicleSuggestTimersRef.current[field]) clearTimeout(vehicleSuggestTimersRef.current[field])
      return
    }

    if (vehicleSuggestTimersRef.current[field]) clearTimeout(vehicleSuggestTimersRef.current[field])
    vehicleSuggestTimersRef.current[field] = setTimeout(async () => {
      try {
        const list = await fetchUschovaNapoveda(apiField, query)
        setVehicleSuggestions((prev) => ({
          ...prev,
          [field]: Array.isArray(list) ? list : [],
        }))
      } catch {
        setVehicleSuggestions((prev) => ({ ...prev, [field]: [] }))
      }
    }, 250)
  }, [])

  const loadPneuSuggestions = useCallback((field, value) => {
    const fieldMap = {
      znacka: 'pneu_znacka',
      rozmer: 'pneu_rozmer',
      model: 'pneu_model',
    }
    const apiField = fieldMap[field]
    if (!apiField) return

    const query = toTrim(value)
    if (query.length < 3) {
      setPneuSuggestions((prev) => ({ ...prev, [field]: [] }))
      if (pneuSuggestTimersRef.current[field]) clearTimeout(pneuSuggestTimersRef.current[field])
      return
    }

    if (pneuSuggestTimersRef.current[field]) clearTimeout(pneuSuggestTimersRef.current[field])
    pneuSuggestTimersRef.current[field] = setTimeout(async () => {
      try {
        const list = await fetchUschovaNapoveda(apiField, query)
        setPneuSuggestions((prev) => ({
          ...prev,
          [field]: Array.isArray(list) ? list.map(toUpper) : [],
        }))
      } catch {
        setPneuSuggestions((prev) => ({ ...prev, [field]: [] }))
      }
    }, 250)
  }, [])

  const loadDetail = useCallback(async () => {
    try {
      setIsLoading(true)
      setError('')
      setDetail({ vehicle: null, customer: null, zakazky: [], pneumatikyByZakazka: [] })

      if (id <= 0) {
        setError('Chybí identifikace záznamu.')
        return
      }

      const data = await fetchUschovaDetail(id)
      setDetail({
        vehicle: data?.vehicle || null,
        customer: data?.customer || null,
        zakazky: Array.isArray(data?.zakazky) ? data.zakazky : [],
        pneumatikyByZakazka: Array.isArray(data?.pneumatikyByZakazka) ? data.pneumatikyByZakazka : [],
      })
    } catch (e) {
      setError(e?.message || 'Nepodařilo se načíst detail úschovy.')
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  const vehicleForm = useMemo(
    () => ({
      spz: toUpper(toTrim(detail.vehicle?.spz)),
      znacka: toTrim(detail.vehicle?.znacka),
      rok_vyroby: toTrim(detail.vehicle?.rok_vyroby),
      typ: vehicleTypeLabel(detail.vehicle?.typ),
      pocet_pneu: toTrim(detail.vehicle?.pocet_pneu),
      poznamka: toTrim(detail.vehicle?.vozidlo_poznamka),
    }),
    [detail.vehicle]
  )

  const customerForm = useMemo(
    () => ({
      firma: toTrim(detail.customer?.firma),
      jmeno: toTrim(detail.customer?.jmeno),
      ulice: toTrim(detail.customer?.ulice),
      psc: toTrim(detail.customer?.psc),
      mesto: toTrim(detail.customer?.obec),
      ico: toTrim(detail.customer?.ico),
      email: toTrim(detail.customer?.email),
      telefon: toTrim(detail.customer?.telefon),
      poznamka: toTrim(detail.customer?.zakaznik_poznamka || detail.customer?.poznamka),
    }),
    [detail.customer]
  )

  const pneuMapByZakazka = useMemo(() => {
    const map = new Map()
    ;(detail.pneumatikyByZakazka || []).forEach((z) => {
      map.set(toNum(z?.id_zakazky), Array.isArray(z?.polozky) ? z.polozky : [])
    })
    return map
  }, [detail.pneumatikyByZakazka])

  const hasPneuRows = useCallback(
    (idZakazky) => (pneuMapByZakazka.get(toNum(idZakazky)) || []).length > 0,
    [pneuMapByZakazka]
  )

  const uskladneneZakazky = useMemo(
    () => (detail.zakazky || []).filter((z) => hasPneuRows(z.id_zakazky) && toNum(z.stav, -1) === 1),
    [detail.zakazky, hasPneuRows]
  )

  const historieZakazky = useMemo(
    () => (detail.zakazky || []).filter((z) => hasPneuRows(z.id_zakazky) && toNum(z.stav, -1) !== 1),
    [detail.zakazky, hasPneuRows]
  )

  const prehledHistorie = useMemo(() => {
    const events = []
    ;(detail.zakazky || []).forEach((zak) => {
      const pneu = pneuMapByZakazka.get(toNum(zak.id_zakazky)) || []
      if (!pneu.length) return
      const common = {
        id_zakazky: toNum(zak.id_zakazky),
        cislo: toTrim(zak.cislo) || '-',
        pocet: pneu.length,
        sezona: toNum(pneu[0]?.sezona, 0),
        disk: toNum(pneu[0]?.disk, 0),
      }
      if (zak.datum_prijmu || zak.datum) {
        events.push({ ...common, typ: 'prijem', datum: zak.datum_prijmu || zak.datum })
      }
      if (zak.datum_vydeje) {
        events.push({ ...common, typ: 'vydej', datum: zak.datum_vydeje })
      }
    })
    return events.sort((a, b) => {
      const dateDiff = dateDaySortValue(b.datum) - dateDaySortValue(a.datum)
      if (dateDiff !== 0) return dateDiff
      const cisloDiff = b.cislo.localeCompare(a.cislo, 'cs', { numeric: true, sensitivity: 'base' })
      if (cisloDiff !== 0) return cisloDiff
      return a.typ === b.typ ? 0 : (a.typ === 'vydej' ? -1 : 1)
    })
  }, [detail.zakazky, pneuMapByZakazka])

  const openVehicleModal = () => {
    setValidatedVehicle(false)
    setVehicleSuggestions({ znacka: [] })
    setVehicleEdit({
      spz: toTrim(detail.vehicle?.spz),
      znacka: toTrim(detail.vehicle?.znacka),
      rok_vyroby: toTrim(detail.vehicle?.rok_vyroby),
      typ: String(toNum(detail.vehicle?.typ, 1)),
      pocet_pneu: toTrim(detail.vehicle?.pocet_pneu),
      poznamka: toTrim(detail.vehicle?.vozidlo_poznamka),
    })
    setShowVehicleModal(true)
  }

  const openCustomerModal = () => {
    setValidatedCustomer(false)
    setCustomerEdit({
      firma: toTrim(detail.customer?.firma),
      jmeno: toTrim(detail.customer?.jmeno),
      ulice: toTrim(detail.customer?.ulice),
      psc: toTrim(detail.customer?.psc),
      mesto: toTrim(detail.customer?.obec),
      ico: toTrim(detail.customer?.ico),
      email: toTrim(detail.customer?.email),
      telefon: toTrim(detail.customer?.telefon),
      poznamka: toTrim(detail.customer?.zakaznik_poznamka || detail.customer?.poznamka),
    })
    setShowCustomerModal(true)
  }

  const handleVehicleEditChange = (e) => {
    const { name, value } = e.target
    const normalizedValue = name === 'spz'
      ? toUpper(value).replace(/\s+/g, '')
      : value
    if (name === 'znacka') {
      loadVehicleSuggestions(name, normalizedValue)
    }
    setVehicleEdit((prev) => ({ ...prev, [name]: normalizedValue }))
  }

  const handleCustomerEditChange = (e) => {
    const { name, value } = e.target
    setCustomerEdit((prev) => ({ ...prev, [name]: value }))
  }

  const openZakazkaModal = (zak) => {
    const rows = (pneuMapByZakazka.get(toNum(zak?.id_zakazky)) || []).map((p, index) => ({
      _canRemove: false,
      pozice: toUpper(toTrim(p.pozice)) || tirePositionForIndex(index),
      znacka: toUpper(toTrim(p.znacka)),
      rozmer: toUpper(toTrim(p.rozmer)),
      model: toUpper(toTrim(p.model)),
      hloubka: formatDepth(p.hloubka),
      sezona: String(toNum(p.sezona, 1)),
      disk: String(toNum(p.disk, 0)),
    }))

    setValidatedZakazka(false)
    setZakazkaMode('edit')
    setZakazkaEdit({
      id_zakazky: toNum(zak?.id_zakazky),
      cislo: toTrim(zak?.cislo),
      pozice: toTrim(zak?.pozice),
      datum: toDateInputValue(zak?.datum_prijmu || zak?.datum),
      poznamka: toTrim(zak?.poznamka),
      tmps: toBool(zak?.tmps),
      poklice: toBool(zak?.poklice),
      srouby: toBool(zak?.srouby),
      polozky: rows.length ? rows : [emptyPneuRow()],
    })
    setPneuSuggestions({ znacka: [], rozmer: [], model: [] })
    setPneuTemplate(emptyPneuTemplate())
    setShowZakazkaModal(true)
  }

  const openPrijemModal = async () => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const datum = `${yyyy}-${mm}-${dd}`

    const anchorZak = (detail.zakazky || []).find((z) => toNum(z?.id_zakazky) === toNum(id))
    const isFirstIntakeOnAnchor = !!anchorZak && !hasPneuRows(anchorZak.id_zakazky)

    let cislo = ''
    let editIdZakazky = 0
    if (isFirstIntakeOnAnchor) {
      cislo = toTrim(anchorZak?.cislo)
      editIdZakazky = toNum(anchorZak?.id_zakazky)
    } else {
      try {
        const res = await fetchUschovaDalsiCislo(datum)
        cislo = toTrim(res?.cislo)
      } catch {
        cislo = ''
      }
    }

    let rows = []
    const historyTemplateZak = (detail.zakazky || [])
      .filter((z) => {
        if (toNum(z?.stav, 1) === 1 || toNum(z?.typ_vydeje, 0) !== 1 || !z?.datum_vydeje) return false
        const src = pneuMapByZakazka.get(toNum(z?.id_zakazky)) || []
        return src.length > 0
      })
      .sort((a, b) => {
        const dateDiff = dateDaySortValue(b.datum_vydeje) - dateDaySortValue(a.datum_vydeje)
        return dateDiff !== 0 ? dateDiff : toNum(b.id_zakazky) - toNum(a.id_zakazky)
      })[0]

    if (historyTemplateZak) {
      const src = pneuMapByZakazka.get(toNum(historyTemplateZak.id_zakazky)) || []
      rows = src.map((p, index) => ({
        _canRemove: false,
        pozice: tirePositionForIndex(index),
        znacka: toUpper(toTrim(p.znacka)),
        rozmer: toUpper(toTrim(p.rozmer)),
        model: toUpper(toTrim(p.model)),
        hloubka: '',
        sezona: String(toNum(p.sezona, 1)),
        disk: String(toNum(p.disk, 0)),
      }))
    } else {
      const pneuCountRaw = toNum(detail.vehicle?.pocet_pneu, 4)
      const pneuCount = Math.min(Math.max(pneuCountRaw, 1), 12)
      rows = Array.from({ length: pneuCount }, (_, index) => emptyPneuRow(tirePositionForIndex(index)))
    }

    setValidatedZakazka(false)
    setZakazkaMode('create')
    setZakazkaEdit({
      id_zakazky: editIdZakazky,
      cislo,
      pozice: editIdZakazky > 0 ? toTrim(anchorZak?.pozice) : '',
      datum,
      poznamka: '',
      tmps: false,
      poklice: false,
      srouby: false,
      polozky: rows,
    })
    setPneuSuggestions({ znacka: [], rozmer: [], model: [] })
    setPneuTemplate(emptyPneuTemplate())
    setShowZakazkaModal(true)
  }

  const handleZakazkaChange = (e) => {
    const { name, value, type, checked } = e.target
    const normalizedValue = name === 'pozice' ? toUpper(value) : value
    setZakazkaEdit((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : normalizedValue }))
  }

  const handleZakazkaPneuChange = (index, field, value) => {
    const normalizedValue = field === 'hloubka'
      ? normalizeDepthInput(value)
      : (field === 'znacka' || field === 'rozmer' || field === 'model' ? toUpper(value) : value)
    if (field === 'znacka' || field === 'rozmer' || field === 'model') {
      loadPneuSuggestions(field, normalizedValue)
    }
    setZakazkaEdit((prev) => {
      const next = [...prev.polozky]
      const old = next[index] || emptyPneuRow()
      next[index] = { ...old, [field]: normalizedValue }

      return { ...prev, polozky: next }
    })
  }

  const handlePneuTemplateChange = (field, value) => {
    const normalizedValue = field === 'hloubka'
      ? normalizeDepthInput(value)
      : (field === 'znacka' || field === 'rozmer' || field === 'model' ? toUpper(value) : value)
    if (field === 'znacka' || field === 'rozmer' || field === 'model') loadPneuSuggestions(field, normalizedValue)
    setPneuTemplate((prev) => ({ ...prev, [field]: normalizedValue }))
    setZakazkaEdit((prev) => ({
      ...prev,
      polozky: (prev.polozky || []).map((row) => ({ ...row, [field]: normalizedValue })),
    }))
  }

  const addZakazkaPneuRow = () => {
    setZakazkaEdit((prev) => {
      const last = prev.polozky?.[prev.polozky.length - 1]
      const nextRow = last
        ? {
            _canRemove: true,
            pozice: tirePositionForIndex(prev.polozky.length),
            znacka: toTrim(last.znacka),
            rozmer: toTrim(last.rozmer),
            model: toTrim(last.model),
            hloubka: '',
            sezona: String(last.sezona ?? ''),
            disk: String(last.disk ?? ''),
          }
        : emptyPneuRow('', true)
      return { ...prev, polozky: [...prev.polozky, nextRow] }
    })
  }

  const removeAddedPneuRow = (index) => {
    setZakazkaEdit((prev) => {
      const rows = Array.isArray(prev.polozky) ? [...prev.polozky] : []
      if (!rows[index]?._canRemove) return prev
      rows.splice(index, 1)
      return { ...prev, polozky: rows }
    })
  }

  const handleSaveVehicle = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (form && !form.checkValidity()) {
      setValidatedVehicle(true)
      return
    }

    try {
      setIsSavingVehicle(true)
      const idVozidla = toNum(detail.vehicle?.id_vozidla)
      if (idVozidla <= 0) throw new Error('Nepodařilo se určit vozidlo pro uložení.')

      await updateUschovaVozidlo(idVozidla, {
        spz: toTrim(vehicleEdit.spz).replace(/\s+/g, '').toUpperCase(),
        znacka: toTrim(vehicleEdit.znacka),
        rok_vyroby: toTrim(vehicleEdit.rok_vyroby),
        typ: toNum(vehicleEdit.typ),
        pocet_pneu: toNum(vehicleEdit.pocet_pneu),
        poznamka: toTrim(vehicleEdit.poznamka),
      })

      setShowVehicleModal(false)
      setValidatedVehicle(false)
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'success', message: 'Vozidlo bylo upraveno.' }])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: err?.message || 'Uložení vozidla se nepodařilo.' }])
    } finally {
      setIsSavingVehicle(false)
    }
  }

  const handleSaveCustomer = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (form && !form.checkValidity()) {
      setValidatedCustomer(true)
      return
    }

    try {
      setIsSavingCustomer(true)
      const idZakaznika = toNum(detail.customer?.id_zakaznika)
      if (idZakaznika <= 0) throw new Error('Nepodařilo se určit zákazníka pro uložení.')

      await updateUschovaZakaznik(idZakaznika, {
        firma: toTrim(customerEdit.firma),
        jmeno: toTrim(customerEdit.jmeno),
        ulice: toTrim(customerEdit.ulice),
        psc: toTrim(customerEdit.psc),
        obec: toTrim(customerEdit.mesto),
        ico: toTrim(customerEdit.ico),
        email: toTrim(customerEdit.email),
        telefon: toTrim(customerEdit.telefon),
        poznamka: toTrim(customerEdit.poznamka),
      })

      setShowCustomerModal(false)
      setValidatedCustomer(false)
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'success', message: 'Zákazník byl upraven.' }])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: err?.message || 'Uložení zákazníka se nepodařilo.' }])
    } finally {
      setIsSavingCustomer(false)
    }
  }

  const handleSaveZakazka = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (form && !form.checkValidity()) {
      setValidatedZakazka(true)
      return
    }

    try {
      setIsSavingZakazka(true)
      const payload = {
        datum: toTrim(zakazkaEdit.datum),
        pozice: toUpper(toTrim(zakazkaEdit.pozice)).slice(0, 50),
        poznamka: toTrim(zakazkaEdit.poznamka),
        tmps: zakazkaEdit.tmps ? 1 : 0,
        poklice: zakazkaEdit.poklice ? 1 : 0,
        srouby: zakazkaEdit.srouby ? 1 : 0,
        polozky: (zakazkaEdit.polozky || []).map((p) => ({
          pozice: toUpper(toTrim(p.pozice)),
          znacka: toUpper(toTrim(p.znacka)),
          rozmer: toUpper(toTrim(p.rozmer)),
          model: toUpper(toTrim(p.model)),
          hloubka: toTrim(p.hloubka).replace(',', '.'),
          sezona: p.sezona === '' ? null : toNum(p.sezona),
          disk: p.disk === '' ? null : toNum(p.disk),
        })),
      }

      if (zakazkaMode === 'create') {
        if (toNum(zakazkaEdit.id_zakazky) > 0) {
          await updateUschovaZakazka(toNum(zakazkaEdit.id_zakazky), payload)
        } else {
          if (id <= 0) throw new Error('Nepodařilo se určit kartu vozidla pro příjem.')
          await createUschovaPrijem({ id_anchor: id, ...payload })
        }
      } else {
        const idZakazky = toNum(zakazkaEdit.id_zakazky)
        if (idZakazky <= 0) throw new Error('Nepodařilo se určit zakázku pro uložení.')
        await updateUschovaZakazka(idZakazky, payload)
      }

      setShowZakazkaModal(false)
      setValidatedZakazka(false)
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'success',
          message: zakazkaMode === 'create' ? 'Nová sada pneumatik byla přijata do úschovy.' : 'Zakázka byla upravena.',
        },
      ])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: err?.message || 'Uložení zakázky se nepodařilo.' }])
    } finally {
      setIsSavingZakazka(false)
    }
  }

  const openIssueModal = (zak, typ) => {
    setIssueCtx({
      id_zakazky: toNum(zak?.id_zakazky),
      cislo: toTrim(zak?.cislo),
      typ: String(typ || '').toLowerCase(),
    })
    setShowIssueModal(true)
  }

  const issueMeta = (typ) => {
    if (typ === 'montaz') return { label: 'MONTÁŽ', variant: 'success', icon: 'bi-wrench', button: 'Montáž na vozidlo' }
    if (typ === 'kufr') return { label: 'DO KUFRU', variant: 'warning', icon: 'bi-bag', button: 'Vrátit zákazníkovi' }
    if (typ === 'likvidace') return { label: 'LIKVIDACE', variant: 'danger', icon: 'bi-trash3', button: 'Předat k likvidaci' }
    return { label: 'VÝDEJ', variant: 'primary', icon: 'bi-box-arrow-up', button: 'Potvrdit výdej' }
  }

  const handleConfirmIssue = async () => {
    try {
      setIsIssuing(true)
      const idZakazky = toNum(issueCtx.id_zakazky)
      if (idZakazky <= 0) throw new Error('Nepodařilo se určit zakázku pro výdej.')

      await issueUschovaZakazka(idZakazky, issueCtx.typ)
      setShowIssueModal(false)
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: 'success', message: `Zakázka ${issueCtx.cislo || ''} byla vydána ze skladu.`.trim() },
      ])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: 'danger', message: err?.message || 'Výdej ze skladu se nepodařil.' },
      ])
    } finally {
      setIsIssuing(false)
    }
  }

  const goToStorages = () => navigate('/storages')
  const currentIssue = issueMeta(issueCtx.typ)

  return (
    <div className="d-grid gap-2">
      {isLoading && (
        <div className="text-muted small d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Načítám detail karty...
        </div>
      )}

      {!!error && (
        <Alert variant="danger" className="mb-0 small">
          {error}
        </Alert>
      )}

      {!isLoading && !error && (
        <Row className="g-3 justify-content-start">
          <Col xxl={4} xl={5} lg={6} md={6} sm={6}>
            <div className="storage-card-surface storage-info-card rounded-3 p-4 h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="fs-5 d-flex align-items-center gap-2">
                  <i className="bi bi-car-front-fill"></i>
                  <span>Vozidlo</span>
                </div>
                <Button variant="outline-primary" size="sm" type="button" onClick={openVehicleModal} title="Upravit vozidlo">
                  <i className="bi bi-pencil-square me-2"></i>Úprava
                </Button>
              </div>
              <ul className="list-unstyled small d-grid gap-2 mb-0">
                <li>
                  <span className="text-muted">SPZ</span>
                  <span className="fs-6 fw-semibold">{vehicleForm.spz || '-'}</span>
                </li>
                <li>
                  <span className="text-muted">Značka / model</span>
                  <span>{vehicleForm.znacka || '-'}</span>
                </li>
                <li><span className="text-muted">Rok výroby</span><span>{vehicleForm.rok_vyroby || '-'}</span></li>
                <li>
                  <span className="text-muted">Typ / počet pneu</span>
                  <span>{`${vehicleForm.typ || '-'} / ${vehicleForm.pocet_pneu || '-'}`}</span>
                </li>
                <li><span className="text-muted">Poznámka</span><span>{vehicleForm.poznamka || '-'}</span></li>
              </ul>
            </div>
          </Col>

          <Col xxl={4} xl={5} lg={6}  md={6} sm={6}>
            <div className="storage-card-surface storage-info-card rounded-3 p-4 h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="fs-5 d-flex align-items-center gap-2">
                  <i className="bi bi-person-fill"></i>
                  <span>Zákazník</span>
                </div>
                <Button variant="outline-primary" size="sm" type="button" onClick={openCustomerModal} title="Upravit zákazníka">
                  <i className="bi bi-pencil-square me-2"></i>Úprava
                </Button>
              </div>
              <ul className="list-unstyled small d-grid gap-2 mb-0">
                <li>
                  <span className="text-muted">Jméno</span>
                  <span className="fs-6 fw-semibold">{customerForm.jmeno || '-'}</span>
                </li>
                <li>
                  <span className="text-muted">Firma / IČ</span>
                  <span>{`${customerForm.firma || '-'}${customerForm.ico ? ` / ${customerForm.ico}` : ''}`}</span>
                </li>
                <li>
                  <span className="text-muted">Adresa</span>
                  <span>{`${customerForm.ulice || ''}, ${customerForm.psc || ''} ${customerForm.mesto || ''}`.replace(/^,\s*/, '').trim() || '-'}</span>
                </li>
                <li><span className="text-muted">E-mail / telefon</span><span>{`${customerForm.email || '-'} / ${customerForm.telefon || '-'}`}</span></li>
                <li><span className="text-muted">Poznámka</span><span>{customerForm.poznamka || '-'}</span></li>
              </ul>
            </div>
          </Col>

          <Col xxl={9} xl={11} lg={12}>
            <div className="mt-3">
              <div className="fs-5 d-flex align-items-center gap-2 ps-3">
                <i className="bi bi-box2-fill"></i>
                <div className="fs-5">Úschovy</div>
              </div>
              <Tabs defaultActiveKey="uskladnene" id="storage-zakazky-tabs" className="my-2">
                <Tab eventKey="uskladnene" title={<span title="Pneu v úschově"><i className="bi bi-box-seam me-2"></i>Uskladněné</span>}>
                  <div className="d-grid gap-2">
                    {uskladneneZakazky.map((zak) => (
                      <StorageZakazkaBlock
                        key={`u-${zak.id_zakazky}`}
                        zak={zak}
                        polozky={pneuMapByZakazka.get(toNum(zak.id_zakazky)) || []}
                        onEdit={openZakazkaModal}
                        onIssue={openIssueModal}
                      />
                    ))}
                    {!uskladneneZakazky.length && (
                      <Alert variant="light" className="d-flex align-items-center small py-3 px-3 mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <span>Aktuálně nejsou uskladněné žádné pneumatiky.</span>
                      </Alert>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="vydane" title={<span title="Přehled vydaných pneu z úschovy"><i className="bi bi-box-arrow-up me-2"></i>Vydané</span>}>
                  <div className="d-grid gap-2">
                    {historieZakazky.map((zak) => (
                      <StorageZakazkaBlock
                        key={`h-${zak.id_zakazky}`}
                        zak={zak}
                        polozky={pneuMapByZakazka.get(toNum(zak.id_zakazky)) || []}
                        history
                        onEdit={openZakazkaModal}
                        onIssue={openIssueModal}
                      />
                    ))}
                    {!historieZakazky.length && (
                      <Alert variant="light" className="d-flex align-items-center small py-3 px-3 mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <span>Zatím nejsou evidované žádné vydané úschovy.</span>
                      </Alert>
                    )}
                  </div>
                </Tab>
                <Tab eventKey="historie" title={<span title="Chronologická historie úschov"><i className="bi bi-clock-history me-2"></i>Historie</span>}>
                  <div className="storage-card-surface rounded-3 p-4">
                    <div className="mb-3">
                      <div className="fs-5 fw-semibold">Přehled úschov</div>
                      <div className="small text-muted">Chronologický souhrn příjmů a výdejů pneumatik podle jednotlivých zakázek.</div>
                    </div>
                    {prehledHistorie.length ? (
                    <Table responsive bordered hover striped size="sm" className="storage-history-table mb-0">
                      <thead>
                        <tr>
                          <th rowSpan={2} className="align-middle">Číslo zakázky</th>
                          <th rowSpan={2} className="align-middle">Datum změny</th>
                          <th colSpan={3} className="text-center">Příjem na sklad</th>
                          <th colSpan={3} className="text-center">Výdej ze skladu</th>
                        </tr>
                        <tr>
                          <th className="text-center">Pneu</th>
                          <th className="text-center">Disk</th>
                          <th className="text-center">Sezóna</th>
                          <th className="text-center">Pneu</th>
                          <th className="text-center">Disk</th>
                          <th className="text-center">Sezóna</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prehledHistorie.map((event, index) => (
                          <tr key={`${event.id_zakazky}-${event.typ}-${index}`}>
                            <td>{event.cislo}</td>
                            <td>{formatDate(event.datum) || '-'}</td>
                            <td className="text-center">{event.typ === 'prijem' ? event.pocet : 0}</td>
                            <td className="text-center">
                              {event.typ === 'prijem' && diskIcons[event.disk] ? <img src={diskIcons[event.disk]} alt={diskLabel(event.disk)} title={diskLabel(event.disk)} /> : null}
                            </td>
                            <td className="text-center">
                              {event.typ === 'prijem' && seasonIcons[event.sezona] ? <img src={seasonIcons[event.sezona]} alt={seasonLabel(event.sezona)} title={seasonLabel(event.sezona)} /> : null}
                            </td>
                            <td className="text-center">{event.typ === 'vydej' ? event.pocet : 0}</td>
                            <td className="text-center">
                              {event.typ === 'vydej' && diskIcons[event.disk] ? <img src={diskIcons[event.disk]} alt={diskLabel(event.disk)} title={diskLabel(event.disk)} /> : null}
                            </td>
                            <td className="text-center">
                              {event.typ === 'vydej' && seasonIcons[event.sezona] ? <img src={seasonIcons[event.sezona]} alt={seasonLabel(event.sezona)} title={seasonLabel(event.sezona)} /> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    ) : (
                    <Alert variant="light" className="d-flex align-items-center small py-3 px-3 mb-0">
                      <i className="bi bi-info-circle me-2"></i><span>Historie je zatím prázdná.</span>
                    </Alert>
                    )}
                  </div>
                </Tab>
              </Tabs>
              <div className="d-flex gap-1 pt-4">
                <Button className="p-2 px-3" type="button" variant="secondary" title="Zpět na přehled úschov" onClick={goToStorages}>
                  <i className="bi bi-arrow-left me-2"></i>Zpět
                </Button>
                <Button className="p-2 px-3" type="button" variant="primary" title="Přidat novou sadu pneu do úschovy" onClick={openPrijemModal}>
                  <i className="bi bi-plus-circle me-2"></i>Příjem
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      )}

      <div className="d-none" data-zakazky={detail.zakazky.length} data-pneu={detail.pneumatikyByZakazka.length}></div>

      <Modal show={showVehicleModal} fullscreen="xl-down" onHide={() => setShowVehicleModal(false)} keyboard={true} centered size="md">
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Vozidlo<br />
            <small className="text-muted fs-6">úprava záznamu</small>
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validatedVehicle} onSubmit={handleSaveVehicle}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-0">
              <Row className="g-2">
                <Col md={4}>
                  <FloatingLabel label={<>spz <span className="text-danger">*</span></>}>
                    <Form.Control
                      required
                      name="spz"
                      value={vehicleEdit.spz}
                      onChange={handleVehicleEditChange}
                      minLength={6}
                      title="Zadejte minimálně 7 znaků."
                      onBlur={(e) =>
                        setVehicleEdit((p) => ({ ...p, spz: toTrim(e.target.value).replace(/\s+/g, '').toUpperCase() }))
                      }
                    />
                  </FloatingLabel>
                </Col>
                <Col md={8}>
                  <FloatingLabel label="značka / model">
                    <Form.Control
                      name="znacka"
                      list="storage-vehicle-znacka-list"
                      maxLength={255}
                      value={vehicleEdit.znacka}
                      onChange={handleVehicleEditChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={3}>
                  <FloatingLabel label="rok výroby">
                    <Form.Control
                      name="rok_vyroby"
                      value={vehicleEdit.rok_vyroby}
                      onChange={handleVehicleEditChange}
                      pattern="^[1-9][0-9]{3}$"
                      title="Uveďte rok ve formátu rrrr."
                      inputMode="numeric"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel label={<>typ <span className="text-danger">*</span></>}>
                    <Form.Select required name="typ" value={vehicleEdit.typ} onChange={handleVehicleEditChange}>
                      <option value="1">osobní</option>
                      <option value="2">suv/4x4</option>
                      <option value="3">dodávka</option>
                      <option value="4">truck</option>
                      <option value="5">moto</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col xs={3}>
                  <FloatingLabel label={<>počet pneu <span className="text-danger">*</span></>}>
                    <Form.Control
                      required
                      name="pocet_pneu"
                      value={vehicleEdit.pocet_pneu}
                      onChange={handleVehicleEditChange}
                      pattern="^[1-9]$"
                      title="Povoleny jsou čísla od 1 do 9."
                      inputMode="numeric"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="poznámka">
                    <Form.Control as="textarea" rows={2} style={{ height: '76px' }} name="poznamka" value={vehicleEdit.poznamka} onChange={handleVehicleEditChange} />
                  </FloatingLabel>
                </Col>
              </Row>
              <datalist id="storage-vehicle-znacka-list">
                {(vehicleSuggestions.znacka || []).map((value, i) => (
                  <option key={`storage-vehicle-znacka-${i}-${value}`} value={value} />
                ))}
              </datalist>
            </Container>
          </Modal.Body>
          <Modal.Footer className="border-0 d-flex justify-content-between px-3 pb-3">
            <div>
              <Button className="me-1" variant="secondary" type="button" onClick={() => setShowVehicleModal(false)}>
                <i className="bi bi-arrow-left me-2"></i>Zpět
              </Button>
              <Button variant="primary" type="submit" disabled={isSavingVehicle}>
                <i className="bi bi-save me-2"></i>{isSavingVehicle ? 'Ukládám...' : 'Uložit'}
              </Button>
            </div>
            <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showCustomerModal} fullscreen="xl-down" onHide={() => setShowCustomerModal(false)} keyboard={true} centered size="md">
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Zákazník<br />
            <small className="text-muted fs-6">úprava záznamu</small>
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validatedCustomer} onSubmit={handleSaveCustomer}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-0">
              <Row className="g-2">
                <Col xs={12}>
                  <FloatingLabel label={<>jméno <span className="text-danger">*</span></>}>
                    <Form.Control required name="jmeno" value={customerEdit.jmeno} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="ulice">
                    <Form.Control name="ulice" value={customerEdit.ulice} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <FloatingLabel label="psč">
                    <Form.Control
                      name="psc"
                      value={customerEdit.psc}
                      onChange={handleCustomerEditChange}
                      pattern="^$|^[0-9]{5}$"
                      title="Pokud je PSČ vyplněno, zadejte přesně 5 číslic."
                      inputMode="numeric"
                      onBlur={(e) => setCustomerEdit((p) => ({ ...p, psc: toTrim(e.target.value).replace(/\s+/g, '') }))}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={8}>
                  <FloatingLabel label="město">
                    <Form.Control name="mesto" value={customerEdit.mesto} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={7}>
                  <FloatingLabel label="e-mail">
                    <Form.Control name="email" value={customerEdit.email} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={5}>
                  <FloatingLabel label="telefon">
                    <Form.Control name="telefon" value={customerEdit.telefon} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={8}>
                  <FloatingLabel label="firma">
                    <Form.Control name="firma" value={customerEdit.firma} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <FloatingLabel label="ič">
                    <Form.Control name="ico" value={customerEdit.ico} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="poznámka">
                    <Form.Control as="textarea" rows={2} style={{ height: '76px' }} name="poznamka" value={customerEdit.poznamka} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
              </Row>
            </Container>
          </Modal.Body>
          <Modal.Footer className="border-0 d-flex justify-content-between px-3 pb-3">
            <div>
              <Button className="me-1" variant="secondary" type="button" onClick={() => setShowCustomerModal(false)}>
                <i className="bi bi-arrow-left me-2"></i>Zpět
              </Button>
              <Button variant="primary" type="submit" disabled={isSavingCustomer}>
                <i className="bi bi-save me-2"></i>{isSavingCustomer ? 'Ukládám...' : 'Uložit'}
              </Button>
            </div>
             <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showZakazkaModal} fullscreen="xl-down" onHide={() => setShowZakazkaModal(false)} keyboard={true} centered size="xl">
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Úschova
            <br />
            <small className="text-muted fs-6">
              {zakazkaMode === 'create' ? `příjem nové sady` : `úprava stávajícího záznamu`}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validatedZakazka} onSubmit={handleSaveZakazka}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-1">
              {zakazkaMode === 'create' && (
                <p className="small text-muted mb-3">
                  Údaje se předvyplní z historie podle poslední sady vydané způsobem <strong>Montáž na vozidlo</strong>.
                  Přenese se vše kromě hloubky vzorku, tu je nutné znovu změřit. Pokud sada v historii není, pole zůstanou prázdná.
                </p>
              )}
              <Row className="g-1 mb-1">
                <Col md={2}>
                  <FloatingLabel label="číslo zakázky">
                    <Form.Control value={zakazkaEdit.cislo} disabled />
                  </FloatingLabel>
                </Col>
                <Col md={2}>
                  <FloatingLabel label="pozice">
                    <Form.Control name="pozice" maxLength={50} value={zakazkaEdit.pozice} onChange={handleZakazkaChange} />
                  </FloatingLabel>
                </Col>
                <Col md={2}>
                  <FloatingLabel label="datum">
                    <Form.Control
                      required
                      type="date"
                      name="datum"
                      value={zakazkaEdit.datum}
                      onChange={handleZakazkaChange}
                    />
                  </FloatingLabel>
                </Col>
              </Row>
              <div className="d-grid gap-1 mt-3">
                <Row className="g-1 align-items-center">
                  <Col md={1}><FloatingLabel label="pozice"><Form.Control value="" disabled aria-label="Hromadná pozice" /></FloatingLabel></Col>
                  <Col md={2}><FloatingLabel label="výrobce"><Form.Control list="storage-pneu-znacka-list" value={pneuTemplate.znacka} onChange={(e) => handlePneuTemplateChange('znacka', e.target.value)} /></FloatingLabel></Col>
                  <Col md={2}><FloatingLabel label="rozměr"><Form.Control list="storage-pneu-rozmer-list" value={pneuTemplate.rozmer} onChange={(e) => handlePneuTemplateChange('rozmer', e.target.value)} /></FloatingLabel></Col>
                  <Col md={3}><FloatingLabel label="model"><Form.Control list="storage-pneu-model-list" value={pneuTemplate.model} onChange={(e) => handlePneuTemplateChange('model', e.target.value)} /></FloatingLabel></Col>
                  <Col md={1}><FloatingLabel label="hloubka"><Form.Control value={pneuTemplate.hloubka} inputMode="decimal" onChange={(e) => handlePneuTemplateChange('hloubka', e.target.value)} /></FloatingLabel></Col>
                  <Col md={1}>
                    <Form.Select className="storage-icon-select" title="Sezóna" aria-label="Hromadná sezóna" value={pneuTemplate.sezona} style={pneuTemplate.sezona ? iconSelectStyle(seasonIcons[pneuTemplate.sezona]) : tireControlStyle} onChange={(e) => handlePneuTemplateChange('sezona', e.target.value)}>
                      <option value="">–</option><option value="1">letní</option><option value="2">zimní</option><option value="3">celoroční</option>
                    </Form.Select>
                  </Col>
                  <Col md={1}>
                    <Form.Select className="storage-icon-select" title="Disk" aria-label="Hromadný disk" value={pneuTemplate.disk} style={pneuTemplate.disk !== '' ? iconSelectStyle(diskIcons[pneuTemplate.disk]) : tireControlStyle} onChange={(e) => handlePneuTemplateChange('disk', e.target.value)}>
                      <option value="">–</option><option value="0">bez</option><option value="1">ocel</option><option value="2">hliník</option>
                    </Form.Select>
                  </Col>
                  <Col className="storage-tire-delete-col"></Col>
                </Row>
                <Row className="g-1">
                  <Col md={11}>
                    <hr className="my-2 border-secondary opacity-50" />
                  </Col>
                  <Col className="storage-tire-delete-col"></Col>
                </Row>
                {zakazkaEdit.polozky.map((p, idx) => (
                  <Row className="g-1 align-items-center" key={`zp-${idx}`}>
                    <Col md={1}>
                      <Form.Control required maxLength={10} style={tireControlStyle} value={p.pozice} aria-label="Pozice pneumatiky" onChange={(e) => handleZakazkaPneuChange(idx, 'pozice', toUpper(e.target.value))} />
                    </Col>
                    <Col md={2}>
                        <Form.Control
                          required
                          style={tireControlStyle}
                          list="storage-pneu-znacka-list"
                          value={p.znacka}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'znacka', e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <Form.Control
                          required
                          style={tireControlStyle}
                          list="storage-pneu-rozmer-list"
                          value={p.rozmer}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'rozmer', e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Control
                          required
                          style={tireControlStyle}
                          list="storage-pneu-model-list"
                          value={p.model}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'model', e.target.value)}
                        />
                    </Col>
                    <Col md={1}>
                        <Form.Control
                          required
                          style={tireControlStyle}
                          value={p.hloubka}
                          pattern="^[0-9]+(,[0-9])?$"
                          title="Zadejte celé číslo nebo číslo s jedním desetinným místem, například 6 nebo 6,1."
                          inputMode="decimal"
                          onChange={(e) => handleZakazkaPneuChange(idx, 'hloubka', e.target.value)}
                        />
                    </Col>
                    <Col md={1}>
                        <Form.Select
                          className="storage-icon-select"
                          title="Sezóna"
                          required
                          value={p.sezona}
                          style={p.sezona !== '' ? iconSelectStyle(seasonIcons[p.sezona]) : tireControlStyle}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'sezona', e.target.value)}
                        >
                          <option value="">–</option>
                          <option value="1">letní</option>
                          <option value="2">zimní</option>
                          <option value="3">celoroční</option>
                        </Form.Select>
                    </Col>
                    <Col md={1}>
                        <Form.Select
                          className="storage-icon-select"
                          title="Disk"
                          required
                          value={p.disk}
                          style={p.disk !== '' ? iconSelectStyle(diskIcons[p.disk]) : tireControlStyle}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'disk', e.target.value)}
                        >
                          <option value="">–</option>
                          <option value="0">bez</option>
                          <option value="1">ocel</option>
                          <option value="2">hliník</option>
                        </Form.Select>
                    </Col>
                    <Col className="storage-tire-delete-col d-flex align-items-center justify-content-center">
                      {p._canRemove && (
                        <Button type="button" className="p-1 text-danger border-0" variant="link" title="Odstranit přidanou pneumatiku" aria-label="Odstranit přidanou pneumatiku" onClick={() => removeAddedPneuRow(idx)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}
              </div>

              <Row className="g-1 mt-1">
                
                <Col md={5}>
                  <FloatingLabel label="poznámka">
                    <Form.Control
                      name="poznamka"
                      value={zakazkaEdit.poznamka}
                      onChange={handleZakazkaChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <div className="h-100 d-flex align-items-center gap-3 px-3">
                    <Form.Check
                      id="zakazka-tmps"
                      type="checkbox"
                      label="tmps"
                      name="tmps"
                      checked={zakazkaEdit.tmps}
                      onChange={handleZakazkaChange}
                    />
                    <Form.Check
                      id="zakazka-poklice"
                      type="checkbox"
                      label="poklice"
                      name="poklice"
                      checked={zakazkaEdit.poklice}
                      onChange={handleZakazkaChange}
                    />
                    <Form.Check
                      id="zakazka-srouby"
                      type="checkbox"
                      label="šrouby"
                      name="srouby"
                      checked={zakazkaEdit.srouby}
                      onChange={handleZakazkaChange}
                    />
                  </div>
                </Col>
              </Row>

              <div className="mt-2">
                <Button type="button" variant="outline-primary" className="px-3" onClick={addZakazkaPneuRow}>
                  <i className="bi bi-plus-circle me-2"></i>Pneu
                </Button>
              </div>
              <datalist id="storage-pneu-znacka-list">
                {(pneuSuggestions.znacka || []).map((value, i) => (
                  <option key={`storage-znacka-${i}-${value}`} value={value} />
                ))}
              </datalist>
              <datalist id="storage-pneu-rozmer-list">
                {(pneuSuggestions.rozmer || []).map((value, i) => (
                  <option key={`storage-rozmer-${i}-${value}`} value={value} />
                ))}
              </datalist>
              <datalist id="storage-pneu-model-list">
                {(pneuSuggestions.model || []).map((value, i) => (
                  <option key={`storage-model-${i}-${value}`} value={value} />
                ))}
              </datalist>
            </Container>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-start px-3 pb-3">
            <div>
              <Button className="me-1" variant="secondary" type="button" onClick={() => setShowZakazkaModal(false)}>
                <i className="bi bi-arrow-left me-2"></i>Zpět
              </Button>
              <Button variant="primary" type="submit" disabled={isSavingZakazka}>
                <i className="bi bi-save me-2"></i>{isSavingZakazka ? 'Ukládám...' : 'Uložit'}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showIssueModal} fullscreen="xl-down" onHide={() => setShowIssueModal(false)} keyboard={true} centered size="md">
        <Modal.Header closeButton className="border-0 px-4 pt-4">
          <Modal.Title className="text-primary lh-1">
            Úschova
            <br />
            <small className="text-muted fs-6">výdej pneu z úschovy</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Alert variant={currentIssue.variant} className="d-flex align-items-center my-2">
            <i className={`bi ${currentIssue.icon} pe-3 fs-3`}></i>
            <div>
              <div>Potvrďte výdej pneumatik z úschovy <strong>{issueCtx.cislo}</strong>.</div>
              <div>Způsob výdeje: <strong>{currentIssue.label}</strong>.</div>
              <div className="small mt-1">Po potvrzení bude záznam přesunut mezi vydané úschovy.</div>
              <div className="small fw-semibold mt-1">Tato změna je nevratná.</div>
            </div>
          </Alert>
          <div className="d-flex gap-1 mt-4">
            <Button variant="secondary" onClick={() => setShowIssueModal(false)} disabled={isIssuing}>
              <i className="bi bi-arrow-left me-2"></i>Zpět
            </Button>
            <Button variant={currentIssue.variant} onClick={handleConfirmIssue} disabled={isIssuing}>
              <i className={`bi ${currentIssue.icon} me-2`}></i>{isIssuing ? 'Zpracovávám...' : currentIssue.button}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Notification notifications={notifications} setNotifications={setNotifications} />
    </div>
  )
}

export default StorageDetail
